/**
 * Environment configuration for the static bundle.
 *
 * Canon docs/ENVIRONMENTS.md: "API base URLs are explicit per environment" and
 * "browser-exposed env values must be safe to publish". Nothing here may become a secret —
 * every value ends up in a public JavaScript file.
 *
 * The URLs are validated at startup rather than trusted, so a mis-built bundle fails loudly
 * instead of quietly pointing a production page at staging data.
 */

export const APP_ENVIRONMENTS = ["local", "staging", "production"] as const;
export type AppEnvironment = (typeof APP_ENVIRONMENTS)[number];

export interface AppConfig {
  environment: AppEnvironment;
  /** Absolute origin (+ optional path) of dealbid-core, never with a trailing slash. */
  apiBaseUrl: string;
  /**
   * Absolute origin of the seller app, where a founder signs in and submits (`D-008`).
   *
   * Explicit per environment for the same reason the API base URL is: a staging site whose
   * submit action led to `app.dealbid.app` would walk a founder out of the isolated
   * environment mid-flow, and nothing downstream could tell that had happened.
   */
  appBaseUrl: string;
}

export interface RawConfig {
  VITE_ENVIRONMENT?: string | undefined;
  VITE_API_BASE_URL?: string | undefined;
  VITE_APP_BASE_URL?: string | undefined;
}

function isAppEnvironment(value: unknown): value is AppEnvironment {
  return (
    typeof value === "string" && (APP_ENVIRONMENTS as readonly string[]).includes(value)
  );
}

/**
 * Plain http is a local-development convenience only. A deployed bundle that talked to core
 * over http would leak seller session cookies in transit, and one that sent a founder to an
 * http seller app would hand their sign-in to the network.
 */
function requireUrl(
  name: keyof RawConfig,
  rawUrl: string | undefined,
  environment: AppEnvironment,
): string {
  if (typeof rawUrl !== "string" || rawUrl.trim() === "") {
    throw new Error(`${name} is required and must be an absolute URL.`);
  }

  let url: URL;
  try {
    url = new URL(rawUrl.trim());
  } catch {
    throw new Error(`${name} must be an absolute URL; received ${rawUrl}.`);
  }

  if (url.protocol !== "https:" && url.protocol !== "http:") {
    throw new Error(`${name} must use http or https; received ${url.protocol}.`);
  }

  if (environment !== "local" && url.protocol !== "https:") {
    throw new Error(`${name} must use https in the ${environment} environment.`);
  }

  return `${url.origin}${url.pathname}`.replace(/\/+$/, "");
}

export function resolveConfig(raw: RawConfig): AppConfig {
  const environment = raw.VITE_ENVIRONMENT;
  if (!isAppEnvironment(environment)) {
    throw new Error(
      `VITE_ENVIRONMENT must be one of ${APP_ENVIRONMENTS.join(", ")}; received ${String(environment)}.`,
    );
  }

  return {
    environment,
    apiBaseUrl: requireUrl("VITE_API_BASE_URL", raw.VITE_API_BASE_URL, environment),
    appBaseUrl: requireUrl("VITE_APP_BASE_URL", raw.VITE_APP_BASE_URL, environment),
  };
}

/**
 * The build-time overrides the harness may set, and the `.env.<mode>` key each replaces.
 *
 * Deliberately **not** the `VITE_` names, and deliberately not including the environment
 * name. See {@link withUrlOverrides}.
 */
export const URL_OVERRIDES = {
  DEALBID_API_BASE_URL: "VITE_API_BASE_URL",
  DEALBID_APP_BASE_URL: "VITE_APP_BASE_URL",
} as const satisfies Readonly<Record<string, keyof RawConfig>>;

/**
 * Lets the build environment redirect one base URL, for the build in front of it only.
 *
 * The cross-repo harness needs this: `dealbid-web`'s local `.env` names the seller app's
 * *dev* port, the harness serves the built bundle on the *preview* port, and without a
 * redirect the public site's submit call to action points at a port with nothing behind it
 * (`WEB-014`, `e2e/harness/config.ts` `buildEnv`). Vite gave it for free by preferring a
 * `VITE_`-prefixed process variable over the `.env.<mode>` file; `WEB-044` moved the build to
 * Next.js, whose config read the file and nothing else, and the redirect stopped arriving
 * without an error. `TEST-005` restored it.
 *
 * ## Why these names and not the `VITE_` ones
 *
 * Reading `process.env.VITE_*` here would be actively wrong, not merely different. `next
 * build` sets `NODE_ENV=production` and loads `.env.production` **into `process.env`** before
 * it evaluates this repository's config — Vite never did that, it kept its `.env` values to
 * itself. So under Next every `VITE_` name in the process environment is ambiguous: it is
 * either something a caller meant, or `.env.production` leaking into a local build. Taking it
 * as an override makes `npm run build:local` compile the *production* URLs into a local
 * bundle, which is how this was found.
 *
 * `DEALBID_`-prefixed names cannot be confused that way. No `.env.<mode>` file defines one,
 * and this is the whole list.
 *
 * ## Why the environment name is not overridable
 *
 * `DEALBID_ENV` already selects which environment a build is, and a second variable able to
 * rename it after the fact is the same ambiguity again. Only the two URLs move, and the
 * result still goes through {@link resolveConfig} — so a redirect is validated exactly as a
 * file value is, and a plain-http URL is still refused outside `local`. It cannot slip past
 * the deploy gate either: `deploy:staging` and `deploy:production` run
 * `check-deploy-config.mjs --verify-dist`, which reads the URLs back out of the *built*
 * bundle rather than out of whatever the build was told.
 */
export function withUrlOverrides(
  raw: RawConfig,
  environment: Readonly<Record<string, string | undefined>>,
): RawConfig {
  const merged: RawConfig = { ...raw };
  for (const [override, key] of Object.entries(URL_OVERRIDES) as [
    keyof typeof URL_OVERRIDES,
    keyof RawConfig,
  ][]) {
    const value = environment[override];
    if (typeof value === "string" && value.trim() !== "") merged[key] = value;
  }
  return merged;
}

let cached: AppConfig | undefined;

/**
 * Memoised so the bundle validates once, at first use.
 *
 * Read from `process.env` rather than `import.meta.env` since `WEB-044` (`D-020`): Next
 * inlines the three values named in `next.config.ts`'s `env` block at build time, so these
 * are literals in the emitted JavaScript by the time a browser sees them — the same
 * substitution Vite performed, by a different build system. Nothing here reaches a real
 * `process` at runtime, and nothing but these three public values is exposed.
 */
export function getConfig(): AppConfig {
  cached ??= resolveConfig({
    VITE_ENVIRONMENT: process.env.VITE_ENVIRONMENT,
    VITE_API_BASE_URL: process.env.VITE_API_BASE_URL,
    VITE_APP_BASE_URL: process.env.VITE_APP_BASE_URL,
  });
  return cached;
}

/** Test seam: drops the memoised value so a case can re-resolve under different variables. */
export function resetConfigCache(): void {
  cached = undefined;
}

/** Joins a `/v1` path onto the configured API base without producing a double slash. */
export function apiUrl(path: string): string {
  const base = getConfig().apiBaseUrl;
  return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
}

/** Joins a path onto the seller app base without producing a double slash. */
export function appUrl(path: string): string {
  const base = getConfig().appBaseUrl;
  return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
}
